import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadSearchMeta, loadSuggestions, searchNavigationState } from "./api";
import { useOptionalFeatureRegistry } from "../feature-status/FeatureRegistryContext";
import { featureMatchesQuery } from "../feature-status/registry";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import EternuaIcon from "../components/EternuaIcon";
import "./GlobalSearchBox.css";

const EMPTY_VISION_FEATURES = [];

export default function GlobalSearchBox({ onNavigate }) {
  useSourceLanguage();
  const location = useLocation();
  const { translateLabel } = useNavigationTranslations();
  const navigate = useNavigate();
  const inputId = useId();
  const listId = useId();
  const sequenceRef = useRef(0);
  const vision = useOptionalFeatureRegistry()?.vision || EMPTY_VISION_FEATURES;
  const metaRequestedRef = useRef(false);
  const [features, setFeatures] = useState({ search: false, autocomplete: false });
  const [query, setQuery] = useState(() => new URLSearchParams(location.search || "").get("q") || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused || metaRequestedRef.current) return undefined;
    metaRequestedRef.current = true;
    const controller = new AbortController();
    loadSearchMeta(controller.signal)
      .then((payload) => setFeatures(payload?.features || {}))
      .catch((error) => {
        if (error?.name === "AbortError") metaRequestedRef.current = false;
        setFeatures({ search: false, autocomplete: false });
      });
    return () => controller.abort();
  }, [focused]);

  useEffect(() => {
    if (location.pathname === "/recherche") {
      setQuery(new URLSearchParams(location.search || "").get("q") || "");
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const clean = query.trim();
    if (!focused || clean.length < 2) {
      setSuggestions((current) => current.length ? [] : current);
      setOpen(false);
      setActiveIndex(-1);
      return undefined;
    }
    const controller = new AbortController();
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    const visionRows = vision
      .filter((feature) => featureMatchesQuery(feature, clean))
      .slice(0, 4)
      .map((feature) => ({ kind: "vision", label: feature.title, url: feature.route, universe: { label: "Vision 3024" } }));
    if (!features.search || !features.autocomplete) {
      setSuggestions(visionRows);
      setOpen(visionRows.length > 0);
      setActiveIndex(-1);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      loadSuggestions(clean, controller.signal)
        .then((payload) => {
          if (sequence !== sequenceRef.current || controller.signal.aborted) return;
          const rows = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
          const combined = [...rows, ...visionRows.filter((visionItem) => !rows.some((row) => row.url === visionItem.url))].slice(0, 8);
          setSuggestions(combined);
          setOpen(combined.length > 0);
          setActiveIndex(-1);
        })
        .catch((error) => {
          if (error?.name === "AbortError" || sequence !== sequenceRef.current) return;
          setSuggestions([]);
          setOpen(false);
        });
    }, 260);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [features.autocomplete, features.search, focused, query, vision]);

  const go = (destination, historyAction = null) => {
    setOpen(false);
    setActiveIndex(-1);
    onNavigate?.();
    const state = searchNavigationState(historyAction);
    navigate(destination, state ? { state } : undefined);
  };

  const submit = (event) => {
    event.preventDefault();
    const clean = query.trim();
    go(clean ? `/recherche?q=${encodeURIComponent(clean)}` : "/recherche", "submit");
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!open || !suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((value) => (value + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((value) => (value <= 0 ? suggestions.length - 1 : value - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      const destination = suggestions[activeIndex].url;
      const isSearchSuggestion = destination.startsWith("/recherche") && !destination.startsWith("/recherche/offres/");
      go(destination, isSearchSuggestion ? "submit" : null);
    }
  };

  return (
    <form className="et-global-search" onSubmit={submit} role="search">
      <label className="et-global-search__label" htmlFor={inputId}>{translateLabel("Rechercher sur Eternua")}</label>
      <div className="et-global-search__field">
        <input
          id={inputId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setFocused(true);
            if (suggestions.length) setOpen(true);
          }}
          onBlur={() => {
            setFocused(false);
            setOpen(false);
          }}
          autoComplete="off"
          placeholder={translateLabel("Rechercher sur Eternua")}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        />
        <button type="submit" aria-label={translateLabel("Lancer la recherche")}><EternuaIcon name="search" /></button>
      </div>
      {open ? (
        <ul id={listId} className="et-global-search__suggestions" role="listbox">
          {suggestions.map((item, index) => (
            <li
              id={`${listId}-${index}`}
              key={`${item.kind}-${item.url}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "is-active" : ""}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                const isSearchSuggestion = item.url.startsWith("/recherche") && !item.url.startsWith("/recherche/offres/");
                go(item.url, isSearchSuggestion ? "submit" : null);
              }}
            >
              <span aria-hidden="true">{item.kind === "vision" ? "✦" : item.kind === "category" ? "◇" : "⌕"}</span>
              <strong>{item.label}</strong>
              {item.universe?.label ? <small>{item.universe.label}</small> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}
